import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster as SonnerToaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SavedCandidatesProvider } from './contexts/SavedCandidatesContext';
import { UnreadMessagesProvider } from './contexts/UnreadMessagesContext';
import { ToastProvider } from './components/Toast';
import { AutoSaveProvider } from './contexts/AutoSaveContext';
import AutoSaveIndicator from './components/AutoSaveIndicator';
import LangAutoDetectToast from './components/LangAutoDetectToast';
import EmployerSectorGate from './components/EmployerSectorGate';
import { SectorProvider } from './contexts/SectorContext';
import PageSkeleton from './components/PageSkeleton';
import ScrollToTop from './components/ScrollToTop';

// Landing is the most-visited route — keep it eagerly imported so the
// first paint never waits for a chunk fetch.
import EditorialLanding from './pages/landing/EditorialLanding';
// Legacy landing kept behind /landing-legacy as a rollback affordance
// during Fase 2 verification (Feb 2026 facelift).
const LegacyLanding = lazy(() => import('./pages/Landing'));

// Every other route is code-split into its own chunk. Webpack auto-names
// them so we get one chunk per page in build/static/js/.
const Login                       = lazy(() => import('./pages/Login'));
const Signup                      = lazy(() => import('./pages/Signup'));
const ForgotPassword              = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword               = lazy(() => import('./pages/ResetPassword'));
const Onboarding                  = lazy(() => import('./pages/Onboarding'));
const EmployerOnboarding          = lazy(() => import('./pages/EmployerOnboarding'));
const EmployerDashboard           = lazy(() => import('./pages/EmployerDashboard'));
const ProfessionalDashboard       = lazy(() => import('./pages/ProfessionalDashboard'));
const AdminDashboard              = lazy(() => import('./pages/AdminDashboard'));
const TeamPortraitsAdmin          = lazy(() => import('./pages/admin/TeamPortraitsAdmin'));
const AdminEmployersPage          = lazy(() => import('./pages/admin/AdminEmployersPage'));
const AdminProfessionalsPage      = lazy(() => import('./pages/admin/AdminProfessionalsPage'));
const AdminAnnouncementsPage      = lazy(() => import('./pages/admin/AdminAnnouncementsPage'));
const AdminGrowthPage             = lazy(() => import('./pages/admin/AdminGrowthPage'));
const AdminAccountsPage           = lazy(() => import('./pages/admin/AdminAccountsPage'));
const AdminEmployersPipelinePage  = lazy(() => import('./pages/admin/AdminEmployersPipelinePage'));
const AdminInstitutionsPage       = lazy(() => import('./pages/admin/AdminInstitutionsPage'));
const KommuneNewsletterPage       = lazy(() => import('./pages/admin/KommuneNewsletterPage'));
const LandbrukPage                = lazy(() => import('./pages/admin/LandbrukPage'));
const MateriellPage               = lazy(() => import('./pages/admin/MateriellPage'));
const AdminProceduresPage         = lazy(() => import('./pages/admin/AdminProceduresPage'));
const ProceduresPage              = lazy(() => import('./pages/ProceduresPage'));
const AdminEmployerStatusesPage   = lazy(() => import('./pages/admin/AdminEmployerStatusesPage'));
const AdminInvestorInquiriesPage  = lazy(() => import('./pages/admin/AdminInvestorInquiriesPage'));
const ForMunicipalities           = lazy(() => import('./pages/ForMunicipalities'));
const BookPrat                    = lazy(() => import('./pages/BookPrat'));
const Kontakt                     = lazy(() => import('./pages/Kontakt'));
const KommuneOversikt             = lazy(() => import('./pages/KommuneOversikt'));
const SectorLanding               = lazy(() => import('./pages/SectorLanding'));
const Nyhetsbrev                  = lazy(() => import('./pages/Nyhetsbrev'));
const NyhetsbrevPameldt           = lazy(() => import('./pages/NyhetsbrevPameldt'));
const OmOss                       = lazy(() => import('./pages/OmOss'));
const Jobs                        = lazy(() => import('./pages/Jobs'));
const JobDetail                   = lazy(() => import('./pages/JobDetail'));
const Profiles                    = lazy(() => import('./pages/Profiles'));
const Messages                    = lazy(() => import('./pages/Messages'));
const Settings                    = lazy(() => import('./pages/Settings'));
const OAuthCallback               = lazy(() => import('./pages/OAuthCallback'));
const Horisont                    = lazy(() => import('./pages/Horisont'));
const HorisontArticle             = lazy(() => import('./pages/HorisontArticle'));
const CreateJob                   = lazy(() => import('./pages/CreateJob'));
const Historier                   = lazy(() => import('./pages/Historier'));
const HistorieDetalj              = lazy(() => import('./pages/HistorieDetalj'));
const OmNorge                     = lazy(() => import('./pages/OmNorge'));
const PreviewAltB                 = lazy(() => import('./pages/PreviewAltB'));
const Profile                     = lazy(() => import('./pages/Profile'));
const CompanyPage                 = lazy(() => import('./pages/CompanyPage'));
const InvestorsPage               = lazy(() => import('./pages/InvestorsPage'));
const TermsPage                   = lazy(() => import('./pages/TermsPage'));
const AdminEmploymentReportsPage  = lazy(() => import('./pages/admin/AdminEmploymentReportsPage'));
const AdminEmailLogPage           = lazy(() => import('./pages/admin/AdminEmailLogPage'));
const AdminInquiries              = lazy(() => import('./pages/AdminInquiries'));
const AdminDemoBookingsPage       = lazy(() => import('./pages/admin/AdminDemoBookingsPage'));
const AdminHireReportsPage        = lazy(() => import('./pages/admin/AdminHireReportsPage'));
const AdminSectorFeesPage         = lazy(() => import('./pages/admin/AdminSectorFeesPage'));
const AdminTermsPage              = lazy(() => import('./pages/admin/AdminTermsPage'));
const AdminDocumentsPage          = lazy(() => import('./pages/admin/AdminDocumentsPage'));
const AdminTendersPage            = lazy(() => import('./pages/admin/AdminTendersPage'));
const AdminTenderInquiriesPage    = lazy(() => import('./pages/admin/AdminTenderInquiriesPage'));
const TendersPublicPage           = lazy(() => import('./pages/TendersPublicPage'));
const PricingPage                 = lazy(() => import('./pages/PricingPage'));
const WhatsNew                    = lazy(() => import('./pages/WhatsNew'));

import TermsAcceptanceModal from './components/TermsAcceptanceModal';

import './App.css';

function ProtectedRoute({ children, role, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Laster...</p>
      </div>
    );
  }

  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  // Single-role gate (legacy): `role="employer"`
  if (role && user.role !== role) {
    return <Navigate to="/dashboard" replace />;
  }

  // Multi-role gate (new): `roles={['employer', 'admin']}`
  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Suspense fallback={<PageSkeleton />}>
      <Routes>
        <Route path="/" element={<EditorialLanding />} />
        <Route path="/landing-legacy" element={<Suspense fallback={<PageSkeleton />}><LegacyLanding /></Suspense>} />
        <Route path="/sektorer" element={<Navigate to="/#sektorer" replace />} />
        <Route path="/sektor/:slug" element={<SectorLanding />} />
        <Route path="/historier" element={<Historier />} />
        <Route path="/historier/:id" element={<HistorieDetalj />} />
        <Route path="/om-norge" element={<OmNorge />} />
        <Route path="/horisont" element={<Horisont />} />
        <Route path="/horisont/:slug" element={<HorisontArticle />} />
        <Route path="/horizon" element={<Horisont />} />
        <Route path="/horizon/:slug" element={<HorisontArticle />} />
        <Route path="/preview/alt-b" element={<PreviewAltB />} />
        <Route path="/for-kommuner" element={<ForMunicipalities />} />
        <Route path="/book-prat" element={<BookPrat />} />
        <Route path="/kontakt" element={<Kontakt />} />
        <Route path="/contact" element={<Kontakt />} />
        <Route path="/for-arbeidsgivere" element={<Navigate to="/for-kommuner" replace />} />
        <Route path="/kommuneoversikt" element={<KommuneOversikt />} />
        <Route path="/nyhetsbrev" element={<Nyhetsbrev />} />
        <Route path="/nyhetsbrev/pameldt" element={<NyhetsbrevPameldt />} />
        <Route path="/om-oss" element={<OmOss />} />
        <Route path="/bedrift/:orgId" element={<CompanyPage />} />
        <Route path="/company/:orgId" element={<CompanyPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/investors" element={<InvestorsPage />} />
        <Route path="/vilkar/fagarbeider" element={<TermsPage />} />
        <Route path="/terms/professional" element={<TermsPage />} />
        <Route path="/vilkar/arbeidsgiver" element={<TermsPage />} />
        <Route path="/vilkar/kommune" element={<TermsPage />} />
        <Route path="/terms/employer" element={<TermsPage />} />
        <Route path="/priser" element={<PricingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/endringslogg" element={<ProtectedRoute roles={['employer', 'admin']}><WhatsNew /></ProtectedRoute>} />
        <Route path="/whats-new" element={<ProtectedRoute roles={['employer', 'admin']}><WhatsNew /></ProtectedRoute>} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/oauth-callback" element={<OAuthCallback />} />

        <Route
          path="/onboarding"
          element={
            <ProtectedRoute role="professional">
              <Onboarding />
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer-onboarding"
          element={
            <ProtectedRoute role="employer">
              <EmployerOnboarding />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              {user && user.role === 'admin' ? (
                <AdminDashboard />
              ) : user && user.role === 'employer' ? (
                <EmployerDashboard />
              ) : (
                <ProfessionalDashboard />
              )}
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/team-portraits"
          element={
            <ProtectedRoute roles={['admin']}>
              <TeamPortraitsAdmin />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/employers"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminEmployersPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/professionals"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminProfessionalsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/announcements"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminAnnouncementsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/vekst"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminGrowthPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/kontoer"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminAccountsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/employers/pipeline"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminEmployersPipelinePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/institusjoner"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminInstitutionsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/nyhetsbrev-kommuner"
          element={
            <ProtectedRoute roles={['admin']}>
              <KommuneNewsletterPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/landbruk"
          element={
            <ProtectedRoute roles={['admin']}>
              <LandbrukPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/materiell"
          element={
            <ProtectedRoute roles={['admin']}>
              <MateriellPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/prosedyrer"
          element={
            <ProtectedRoute>
              <AdminProceduresPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/prosedyrer"
          element={
            <ProtectedRoute>
              <ProceduresPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/employer-statuses"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminEmployerStatusesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/investor-inquiries"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminInvestorInquiriesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/employment-reports"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminEmploymentReportsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/henvendelser"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminInquiries />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/demo-bookinger"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminDemoBookingsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/email-log"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminEmailLogPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/hire-reports"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminHireReportsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/sector-fees"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminSectorFeesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/terms"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminTermsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/documents"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminDocumentsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/anbud"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminTendersPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/tender-inquiries"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminTenderInquiriesPage />
            </ProtectedRoute>
          }
        />

        {/* OPPGAVE 8 — Public tender monitor (no auth) */}
        <Route path="/anbud" element={<TendersPublicPage />} />
        <Route path="/tenders" element={<TendersPublicPage />} />

        <Route
          path="/profil"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={<Navigate to="/profil" replace />}
        />

        <Route
          path="/jobs"
          element={
            <ProtectedRoute>
              <Jobs />
            </ProtectedRoute>
          }
        />

        <Route
          path="/jobs/new"
          element={
            <ProtectedRoute role="employer">
              <CreateJob />
            </ProtectedRoute>
          }
        />

        <Route
          path="/jobs/:jobId"
          element={
            <ProtectedRoute>
              <JobDetail />
            </ProtectedRoute>
          }
        />

        <Route
          path="/profiles"
          element={
            <ProtectedRoute roles={['employer', 'admin']}>
              <Profiles />
            </ProtectedRoute>
          }
        />

        <Route
          path="/messages"
          element={
            <ProtectedRoute>
              <Messages />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <ToastProvider>
        <AutoSaveProvider>
          <ThemeProvider>
            <LanguageProvider>
              <AuthProvider>
                <SavedCandidatesProvider>
                  <UnreadMessagesProvider>
                    <SectorProvider>
                      <EmployerSectorGate>
                        <AppRoutes />
                      </EmployerSectorGate>
                    </SectorProvider>
                    <LangAutoDetectToast />
                    <TermsAcceptanceModal />
                    <AutoSaveIndicator />
                    <SonnerToaster position="bottom-right" richColors closeButton />
                  </UnreadMessagesProvider>
                </SavedCandidatesProvider>
              </AuthProvider>
            </LanguageProvider>
          </ThemeProvider>
        </AutoSaveProvider>
      </ToastProvider>
    </Router>
  );
}

export default App;
